import { useState, useEffect, useRef } from 'react';

/**
 * Resolves once the browser has the font available for measuring. Canvas
 * measureText never triggers a web-font download, so without this the first
 * card (or the first card after a font switch) is measured in the fallback
 * font and drawn in the real one. Environments without the CSS Font Loading
 * API (jsdom) resolve immediately; a failed load resolves too — measuring in
 * the fallback beats never measuring.
 */
const fontReady = (font) => {
    if (typeof document === 'undefined' || !document.fonts || typeof document.fonts.load !== 'function') {
        return Promise.resolve();
    }
    try {
        return document.fonts.load(`normal 100px "${font}"`).then(() => undefined, () => undefined);
    } catch (e) {
        return Promise.resolve();
    }
};

/**
 * A hook that calculates the maximum font-size in pixels that fits within a container.
 *
 * Two modes:
 *   - per item (default, unchanged): `text` is fitted on its own — the size the
 *     word cards (HFW / CVC / 4K Readiness / the screener and their Practice
 *     decks) want, where each word fills the card.
 *   - uniform (v3.85.0, `options.deck`): ONE size for the whole deck of a
 *     letter run — the largest size at which every glyph in `deck` fits the
 *     box, measured against the deck's tallest ascender and deepest descender
 *     together (so 'l', 'g' and 'x' cards are the same size and sit on one
 *     baseline), computed once per (font, box, deck) after the font has
 *     loaded, and again on resize or a font change. `text` no longer drives
 *     a recalculation: turning a card never resizes or blinks.
 *
 * @param {string} text The text content that will be rendered
 * @param {string} font The font family string
 * @param {number} minFontSize Minimum font size in px
 * @param {number} maxFontSize Maximum font size in px
 * @param {{ deck?: string[] }} [options] `deck`: every glyph string of the run → uniform mode
 * @returns {Object} { ref: React.RefObject, fontSize: number, centerOffset: number, isCalculating: boolean }
 */
const useFitText = (text, font = 'Teachers', minFontSize = 10, maxFontSize = 1000, options = undefined) => {
    const [containerElement, setContainerElement] = useState(null);
    const [fontSize, setFontSize] = useState(null);
    const [centerOffset, setCenterOffset] = useState(0);
    const [isCalculating, setIsCalculating] = useState(true);

    const deck = options && Array.isArray(options.deck) && options.deck.length > 0
        ? options.deck.map((d) => String(d)).filter((d) => d.length > 0)
        : null;
    const uniform = !!deck && deck.length > 0;
    // The deck's identity is its content: a fresh array each render must not re-measure.
    const deckSignature = uniform ? deck.join('\u0001') : null;
    const deckRef = useRef(deck);
    deckRef.current = deck;
    // Per-item mode measures on every text change; uniform mode ignores text.
    const measureKey = uniform ? deckSignature : text;

    useEffect(() => {
        // Reset state when the measured input or font changes to trigger fresh calculation visibility guard
        setFontSize(null);
        setCenterOffset(0);
        setIsCalculating(true);

        if (!containerElement) return;
        let cancelled = false;

        const measurePerItem = (context, width, height) => {
            // Binary search to find the optimal pixel size
            let min = minFontSize;
            let max = maxFontSize;
            let bestFit = min;

            while (min <= max) {
                const mid = Math.floor((min + max) / 2);
                context.font = `normal ${mid}px ${font}`;

                // Measure width precisely for the target glyph
                const widthMetrics = context.measureText(text);
                const textWidth = widthMetrics.width;

                // Measure physical ink top and bottom boundaries explicitly.
                const inkMetrics = context.measureText(text);
                const inkAscent = inkMetrics.actualBoundingBoxAscent;
                const inkDescent = inkMetrics.actualBoundingBoxDescent;

                // 1. Ensure text doesn't overflow horizontally (with 10% padding).
                // 2. Ensure absolute total ink height doesn't exceed 90% of container height.
                // By removing absolute Y boundaries and instead shifting the ink later,
                // we allow the font to grow much significantly larger!
                if (
                    textWidth <= width * 0.90 &&
                    (inkAscent + inkDescent) <= height * 0.90
                ) {
                    bestFit = mid;
                    min = mid + 1; // Try bigger
                } else {
                    max = mid - 1; // Too big, go smaller
                }
            }

            // Calculate the exact pixel shift required to re-center the physical black ink
            // in the absolute middle of the flashcard container.
            context.font = `normal ${bestFit}px ${font}`;
            const finalInkMetrics = context.measureText(text);
            const finalInkAscent = finalInkMetrics.actualBoundingBoxAscent;
            const finalInkDescent = finalInkMetrics.actualBoundingBoxDescent;
            const finalFontAscent = finalInkMetrics.fontBoundingBoxAscent || finalInkAscent;
            const finalFontDescent = finalInkMetrics.fontBoundingBoxDescent || finalInkDescent;

            // Flexbox baseline offset math simplified for translateY shift
            // translateY = (fontDescent - fontAscent + inkAscent - inkDescent) / 2
            const shiftY = (finalFontDescent - finalFontAscent + finalInkAscent - finalInkDescent) / 2;
            return { size: bestFit, shiftY };
        };

        // Uniform mode: the deck's union ink box — widest advance, tallest
        // ascender, deepest descender across every glyph — scaled to the box.
        // Metrics scale linearly with the font size, so one pass at a
        // reference size gives the answer; the final measure at that size
        // re-checks it (hinting can round) and yields the common shift.
        const unionMetrics = (context, size, items) => {
            context.font = `normal ${size}px ${font}`;
            let widest = 0;
            let ascent = 0;
            let descent = 0;
            let fontAscent = 0;
            let fontDescent = 0;
            for (const item of items) {
                const m = context.measureText(item);
                widest = Math.max(widest, m.width);
                ascent = Math.max(ascent, m.actualBoundingBoxAscent || 0);
                descent = Math.max(descent, m.actualBoundingBoxDescent || 0);
                fontAscent = Math.max(fontAscent, m.fontBoundingBoxAscent || m.actualBoundingBoxAscent || 0);
                fontDescent = Math.max(fontDescent, m.fontBoundingBoxDescent || m.actualBoundingBoxDescent || 0);
            }
            return { widest, ascent, descent, fontAscent, fontDescent };
        };

        const measureUniform = (context, width, height) => {
            const items = deckRef.current || [];
            const REF = 100;
            const ref = unionMetrics(context, REF, items);
            const byWidth = ref.widest > 0 ? (width * 0.90) / (ref.widest / REF) : maxFontSize;
            const inkHeight = ref.ascent + ref.descent;
            const byHeight = inkHeight > 0 ? (height * 0.90) / (inkHeight / REF) : maxFontSize;
            let size = Math.floor(Math.min(byWidth, byHeight));
            size = Math.max(minFontSize, Math.min(maxFontSize, size));
            // Re-check at the chosen size; step down while the union box overflows.
            let final = unionMetrics(context, size, items);
            while (size > minFontSize && (final.widest > width * 0.90 || (final.ascent + final.descent) > height * 0.90)) {
                size -= 1;
                final = unionMetrics(context, size, items);
            }
            const fontAscent = final.fontAscent || final.ascent;
            const fontDescent = final.fontDescent || final.descent;
            // The deck's union ink box is what gets centred — every glyph then
            // shares one baseline inside a box sized for the whole run.
            const shiftY = (fontDescent - fontAscent + final.ascent - final.descent) / 2;
            return { size, shiftY };
        };

        const resizeObserver = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid ResizeObserver loop limit exceeded error
            window.requestAnimationFrame(() => {
                if (cancelled) return;
                if (!entries || entries.length === 0) return;

                const container = containerElement;
                const width = container.clientWidth;
                const height = container.clientHeight;

                if (width === 0 || height === 0) return;

                const run = () => {
                    if (cancelled) return;
                    // Create a hidden canvas to measure exact text dimensions rapidly
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    if (!context) return;
                    const { size, shiftY } = uniform
                        ? measureUniform(context, width, height)
                        : measurePerItem(context, width, height);
                    setFontSize(size);
                    setCenterOffset(shiftY);
                    setIsCalculating(false);
                };

                if (uniform) {
                    fontReady(font).then(run);
                } else {
                    run();
                }
            });
        });

        resizeObserver.observe(containerElement);

        return () => {
            cancelled = true;
            resizeObserver.disconnect();
        };
        // `text` is read through measureKey in per-item mode and deliberately
        // ignored in uniform mode (the deck is the input); the deck itself is
        // read through deckRef so a fresh array never re-measures.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureKey, uniform, font, minFontSize, maxFontSize, containerElement]);

    return { ref: setContainerElement, fontSize, centerOffset, isCalculating };
};

export default useFitText;
